import { Controller } from "@hotwired/stimulus";
import { Dropzone } from "dropzone";

export default class extends Controller {
  static targets = ["input"];
  static values = {
    acceptedFiles: { type: String, default: "image/*" },
    maxFilesize: { type: Number, default: 5 },
    maxFiles: { type: Number, default: 1 },
  };

  connect() {
    console.log("Dropzone controller connected for Shrine");
    this.hideFileInput();
    this.createDropZone();
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }

  hideFileInput() {
    this.inputTarget.style.display = "none";
  }

  createDropZone() {
    const dropZone = this;

    this.dropzone = new Dropzone(this.element, {
      url: "#", // We're not using the URL since we'll handle file locally
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFilesizeValue,
      acceptedFiles: this.acceptedFilesValue,
      addRemoveLinks: false, // Using custom remove element in previewTemplate
      autoQueue: false,
      autoProcessQueue: false,

      dictDefaultMessage: `
        <div class="text-center py-6">
          <svg class="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div class="mt-2">
            <span class="font-medium text-fmt-blue dark:text-fmt-blue hover:text-fmt-blue-dark dark:hover:text-blue-400">Click to upload</span>
            <span class="text-neutral-600 dark:text-neutral-400"> or drag and drop</span>
          </div>
          <p class="text-xs text-neutral-500 dark:text-neutral-400 mt-1">PNG, JPG, GIF, WebP up to ${this.maxFilesizeValue}MB</p>
        </div>
      `,

      previewTemplate: `
        <div class="dz-preview dz-file-preview">
          <div class="dz-image"><img data-dz-thumbnail /></div>
          <div class="dz-details">
            <div class="dz-size"><span data-dz-size></span></div>
            <div class="dz-filename"><span data-dz-name></span></div>
          </div>
          <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
          <div class="dz-error-message"><span data-dz-errormessage></span></div>
          <div class="dz-success-mark">✓</div>
          <div class="dz-error-mark">✗</div>
          <div class="dz-remove" data-dz-remove>Remove file</div>
        </div>
      `,

      init: function () {
        this.on("addedfile", (file) => {
          console.log("File added:", file.name);

          // Show the dropzone if it was hidden
          if (dropZone.element.classList.contains("hidden")) {
            dropZone.element.classList.remove("hidden");
          }

          // Replace the file input with the new file
          dropZone.replaceFileInput(file);

          // Mark as success immediately since we're handling it locally
          this.emit("success", file);
          this.emit("complete", file);
        });

        this.on("removedfile", (file) => {
          console.log("File removed:", file.name);

          // Clear the file input
          dropZone.clearFileInput();

          // Hide the dropzone again if no files and it was originally hidden
          if (this.files.length === 0) {
            const hasExistingImage = dropZone.element
              .closest("div")
              .querySelector("img");
            if (hasExistingImage) {
              dropZone.element.classList.add("hidden");
            }
          }
        });
      },
    });

    return this.dropzone;
  }

  replaceFileInput(file) {
    // Create a new file input to replace the hidden one
    const dataTransfer = new DataTransfer();

    // Add the file to the DataTransfer object
    dataTransfer.items.add(file);

    // Set the files property of the input
    this.inputTarget.files = dataTransfer.files;

    console.log("File input updated with:", file.name);
  }

  clearFileInput() {
    // Clear the file input
    this.inputTarget.value = "";
    console.log("File input cleared");
  }
}
